<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { toast } from 'vue-sonner';
import { useQueryClient } from '@tanstack/vue-query';
import { useAsyncOperationSubscription } from '@/composables/useAsyncOperationSubscription';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import InspectorTabsMenu from '@/components/DataTable/InspectorTabsMenu.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import CoverArt from '@/components/CoverArt.vue';
import LiveTrackCard from '@/components/LiveTrackCard.vue';
import InspectorDetailList from '@/components/InspectorDetailList.vue';
import InspectorDetailRow from '@/components/InspectorDetailRow.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import SeverityBadge from '@/components/AudioFileIssues/SeverityBadge.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
import { issueDetailEntries } from '@/components/AudioFileIssues/issueDetailEntries';
import {
    useApplyAudioFix,
    useFixUploadPresign,
    useRerunAudioFileIssues,
    useAudioFileIssuesForTrack,
    useAudioFileIssueDdexMatches,
    useLinkRawDdexToLiveTrack,
    useRerunAudioFileIssueDdexMatches,
    ROOT_KEY,
    fetchBackupPresignedDescriptor,
    downloadPresignedUrlAsBlob,
    type AudioFileIssueDdexMatchRow,
    type AudioFileIssueRow,
    type AudioFileIssueSeverity,
} from '@/composables/api/useAudioFileIssuesApi';
import { fetchRawDdexTrackAudioDescriptor } from '@/composables/api/useTrackAudioApi';
import { useAudioPlaybackStore } from '@/stores/playerStore';
import { parseBackupMp3Metadata, type BackupMp3TagSummary } from '@/utils/backupMp3Metadata';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import {
    scopedPendingStaticQueryKey,
    useScopedPendingAsyncOperation,
} from '@/composables/api/useAsyncOperationsApi';

const props = defineProps<{
    trackCode: string;
}>();

const codeRef = computed(() => props.trackCode);
const normalizedTrackCode = computed(() => props.trackCode.trim().toUpperCase());

const { data, isLoading, error, refetch } = useAudioFileIssuesForTrack(codeRef);
const {
    data: ddexMatches,
    isLoading: ddexMatchesLoading,
    error: ddexMatchesError,
    refetch: refetchDdexMatches,
} = useAudioFileIssueDdexMatches(codeRef);

watch(
    () => props.trackCode,
    () => {
        if (props.trackCode) {
            refetch();
            refetchDdexMatches();
        }
    }
);

const rerunMutation = useRerunAudioFileIssues();
const ddexRerunMutation = useRerunAudioFileIssueDdexMatches();
const queryClient = useQueryClient();
const ddexRerunOperationId = ref<string | null>(null);
/** Live track row that triggered the rerun; kept when navigating to another inspector row until Pusher completes. */
const ddexRerunQueuedForTrackCodeUpper = ref<string | null>(null);
/** Narrow POST re-run requests to one inspector row (mutation pending is shared). */
const ddexRerunPostRequestedForCodeUpper = ref<string | null>(null);
/** Operation ids we've already finalized — guards against double-toasting from Echo + polling. */
const ddexRerunFinalizedOperationIds = new Set<string>();

const DDEX_MATCH_RERUN_ASYNC_TYPE = 'ddex_match_rerun';

const ddexScopedPendingEnabled = computed(() => normalizedTrackCode.value.trim() !== '');
const { data: ddexScopedPendingData } = useScopedPendingAsyncOperation(
    DDEX_MATCH_RERUN_ASYNC_TYPE,
    normalizedTrackCode,
    ddexScopedPendingEnabled,
);

const VERIFICATION_RERUN_ASYNC_TYPE = 'audio_file_verification_rerun';

const reverifyRerunOperationId = ref<string | null>(null);
/** Live track row that triggered the re-verify; kept across navigation until the op finishes. */
const reverifyQueuedForTrackCodeUpper = ref<string | null>(null);
/** Narrow POST re-verify requests to one inspector row (mutation pending is shared). */
const reverifyPostRequestedForCodeUpper = ref<string | null>(null);
/** Operation ids already finalized — guards against double-toasting from Echo + polling. */
const reverifyFinalizedOperationIds = new Set<string>();

const reverifyScopedPendingEnabled = computed(() => normalizedTrackCode.value.trim() !== '');
const { data: reverifyScopedPendingData } = useScopedPendingAsyncOperation(
    VERIFICATION_RERUN_ASYNC_TYPE,
    normalizedTrackCode,
    reverifyScopedPendingEnabled,
);

/**
 * Finalize a re-verify whether it arrived via Echo broadcast or the polling
 * fallback. Toasts once per operation id; either path can win the race when
 * Pusher is flaky or the `afterResponse()` job dispatch beats the subscribe.
 */
function finalizeReverify(payload: {
    operation_id: string;
    status?: string;
    error_code?: string | null;
    meta?: Record<string, unknown>;
}): void {
    if (payload.operation_id === '' || reverifyFinalizedOperationIds.has(payload.operation_id)) {
        return;
    }
    reverifyFinalizedOperationIds.add(payload.operation_id);

    if (reverifyRerunOperationId.value === payload.operation_id) {
        reverifyRerunOperationId.value = null;
    }

    const metaRaw = payload.meta?.live_track_code;
    const fromMeta =
        typeof metaRaw === 'string' && metaRaw.trim() !== ''
            ? metaRaw.trim().toUpperCase()
            : null;
    const codeUpper =
        reverifyQueuedForTrackCodeUpper.value ?? fromMeta ?? normalizedTrackCode.value;
    reverifyQueuedForTrackCodeUpper.value = null;

    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'by-track', codeUpper] });
    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'list'] });
    void queryClient.invalidateQueries({
        queryKey: scopedPendingStaticQueryKey(VERIFICATION_RERUN_ASYNC_TYPE, codeUpper),
    });

    if (payload.status === 'succeeded') {
        toast.success('Re-verification complete.');
    } else if (payload.status === 'failed') {
        toast.error(
            payload.error_code
                ? `Re-verification failed (${payload.error_code})`
                : 'Re-verification failed.'
        );
    }
}

watch(
    () => reverifyScopedPendingData.value,
    (row) => {
        const ours = normalizedTrackCode.value;
        if (
            row?.pending === true &&
            typeof row.operation_id === 'string' &&
            row.operation_id !== ''
        ) {
            if (!reverifyFinalizedOperationIds.has(row.operation_id)) {
                reverifyRerunOperationId.value = row.operation_id;
                reverifyQueuedForTrackCodeUpper.value = ours;
            }
            return;
        }

        if (!row || row.pending !== false) {
            return;
        }

        // Backend retained the row with terminal status — finalize from polling. Guard with
        // "did we track this in-session" so a fresh page-load within the terminal TTL doesn't
        // toast a result the user didn't kick off in this tab.
        if (
            typeof row.operation_id === 'string' &&
            row.operation_id !== '' &&
            (row.status === 'succeeded' || row.status === 'failed') &&
            reverifyQueuedForTrackCodeUpper.value === ours &&
            reverifyRerunOperationId.value !== null
        ) {
            finalizeReverify({
                operation_id: row.operation_id,
                status: row.status,
                error_code: row.error_code ?? null,
                meta: row.meta,
            });
            return;
        }

        // Row gone (terminal TTL elapsed) but we were tracking one — silent cleanup.
        if (
            ours !== '' &&
            reverifyQueuedForTrackCodeUpper.value === ours &&
            reverifyRerunOperationId.value !== null
        ) {
            reverifyRerunOperationId.value = null;
            reverifyQueuedForTrackCodeUpper.value = null;
        }
    },
    { flush: 'post' },
);

useAsyncOperationSubscription(reverifyRerunOperationId, {
    onFinished: (payload) => {
        if (payload.type !== VERIFICATION_RERUN_ASYNC_TYPE) {
            return;
        }
        finalizeReverify({
            operation_id: payload.operation_id,
            status: payload.status,
            error_code: payload.error_code ?? null,
            meta: payload.meta,
        });
    },
});

/**
 * Finalize a rerun whether it arrived via Echo broadcast or the polling fallback.
 * Toasts once per operation id; either path can win the race when Pusher is flaky
 * or the `afterResponse()` job dispatch beats the SPA's subscribe handshake.
 */
function finalizeDdexRerun(payload: {
    operation_id: string;
    status?: string;
    error_code?: string | null;
    meta?: Record<string, unknown>;
}): void {
    if (payload.operation_id === '' || ddexRerunFinalizedOperationIds.has(payload.operation_id)) {
        return;
    }
    ddexRerunFinalizedOperationIds.add(payload.operation_id);

    if (ddexRerunOperationId.value === payload.operation_id) {
        ddexRerunOperationId.value = null;
    }

    const metaRaw = payload.meta?.live_track_code;
    const fromMeta =
        typeof metaRaw === 'string' && metaRaw.trim() !== ''
            ? metaRaw.trim().toUpperCase()
            : null;
    const codeUpper =
        ddexRerunQueuedForTrackCodeUpper.value ?? fromMeta ?? normalizedTrackCode.value;
    ddexRerunQueuedForTrackCodeUpper.value = null;

    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'ddex-matches', codeUpper] });
    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'by-track', codeUpper] });
    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'summary'] });
    void queryClient.invalidateQueries({ queryKey: [...ROOT_KEY, 'list'] });
    void queryClient.invalidateQueries({ queryKey: ['trackMatching'] });
    void queryClient.invalidateQueries({
        queryKey: scopedPendingStaticQueryKey(DDEX_MATCH_RERUN_ASYNC_TYPE, codeUpper),
    });

    if (payload.status === 'succeeded') {
        toast.success('DDEX matching finished');
    } else if (payload.status === 'failed') {
        toast.error(
            payload.error_code
                ? `DDEX matching failed (${payload.error_code})`
                : 'DDEX matching failed'
        );
    }
}

watch(
    () => ddexScopedPendingData.value,
    (row) => {
        const ours = normalizedTrackCode.value;
        if (
            row?.pending === true &&
            typeof row.operation_id === 'string' &&
            row.operation_id !== ''
        ) {
            if (!ddexRerunFinalizedOperationIds.has(row.operation_id)) {
                ddexRerunOperationId.value = row.operation_id;
                ddexRerunQueuedForTrackCodeUpper.value = ours;
            }
            return;
        }

        if (!row || row.pending !== false) {
            return;
        }

        // Backend retained the row with terminal status (succeeded|failed) — finalize from polling.
        // Guard with "did we track this in-session" so a fresh page-load within the terminal TTL
        // doesn't toast a result the user didn't kick off in this tab.
        if (
            typeof row.operation_id === 'string' &&
            row.operation_id !== '' &&
            (row.status === 'succeeded' || row.status === 'failed') &&
            ddexRerunQueuedForTrackCodeUpper.value === ours &&
            ddexRerunOperationId.value !== null
        ) {
            finalizeDdexRerun({
                operation_id: row.operation_id,
                status: row.status,
                error_code: row.error_code ?? null,
                meta: row.meta,
            });
            return;
        }

        // Row gone (e.g. terminal-state TTL elapsed) but we were tracking one — silent cleanup.
        if (
            ours !== '' &&
            ddexRerunQueuedForTrackCodeUpper.value === ours &&
            ddexRerunOperationId.value !== null
        ) {
            ddexRerunOperationId.value = null;
            ddexRerunQueuedForTrackCodeUpper.value = null;
        }
    },
    { flush: 'post' },
);

useAsyncOperationSubscription(ddexRerunOperationId, {
    onFinished: (payload) => {
        if (payload.type !== 'ddex_match_rerun') {
            return;
        }
        finalizeDdexRerun({
            operation_id: payload.operation_id,
            status: payload.status,
            error_code: payload.error_code ?? null,
            meta: payload.meta,
        });
    },
});

const presignMutation = useFixUploadPresign();
const applyMutation = useApplyAudioFix();
const linkDdexMutation = useLinkRawDdexToLiveTrack();
const playback = useAudioPlaybackStore();

const manualFileInputRef = ref<HTMLInputElement | null>(null);
const uploading = ref(false);

/**
 * Tabs that the inspector currently exposes. The persisted value is validated
 * against this list so a renamed/removed tab in a later release silently falls
 * back to the default instead of stranding users on a dead value.
 */
const KNOWN_INSPECTOR_TABS = ['issues', 'recover-backup', 'manual-upload', 'ddex'] as const;
type InspectorTabValue = (typeof KNOWN_INSPECTOR_TABS)[number];

const INSPECTOR_TAB_ITEMS: { value: InspectorTabValue; label: string }[] = [
    { value: 'issues', label: 'Issues' },
    { value: 'recover-backup', label: 'Recover Backup' },
    { value: 'manual-upload', label: 'Manual Upload' },
    { value: 'ddex', label: 'Ddex Matches' },
];

function onInspectorTabSelect(value: string | number) {
    if ((KNOWN_INSPECTOR_TABS as readonly string[]).includes(String(value))) {
        inspectorTab.value = value as InspectorTabValue;
    }
}

const INSPECTOR_TAB_DEFAULT: InspectorTabValue = 'issues';
const INSPECTOR_TAB_STORAGE_KEY = 'audio-file-issue-inspector:tab:v1';

function readPersistedInspectorTab(): InspectorTabValue {
    if (typeof window === 'undefined') {
        return INSPECTOR_TAB_DEFAULT;
    }
    try {
        const storedValue = window.localStorage.getItem(INSPECTOR_TAB_STORAGE_KEY);
        if (
            storedValue !== null &&
            (KNOWN_INSPECTOR_TABS as readonly string[]).includes(storedValue)
        ) {
            return storedValue as InspectorTabValue;
        }
    } catch {
        // SecurityError / quota / malformed storage all fall back to the default.
    }

    return INSPECTOR_TAB_DEFAULT;
}

const inspectorTab = ref<InspectorTabValue>(readPersistedInspectorTab());

watch(inspectorTab, (nextTab) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(INSPECTOR_TAB_STORAGE_KEY, nextTab);
    } catch {
        // Quota / private-mode writes shouldn't break the tab switch.
    }
});

type LoadBackupPhase = 'idle' | 'presign' | 'download' | 'parse' | 'done' | 'error';

const loadBackupPhase = ref<LoadBackupPhase>('idle');
const backupDownloadLoaded = ref(0);
const backupDownloadTotal = ref<number | null>(null);
const backupTags = ref<BackupMp3TagSummary | null>(null);
const loadBackupInFlight = ref(false);

const manualUploadTags = ref<BackupMp3TagSummary | null>(null);
const pendingManualFile = ref<File | null>(null);
/** Manual upload: tag parse runs after pick; upload stays available even when parsing fails (file is kept). */
const manualUploadParsing = ref(false);

const ddexPlayLoadingId = ref<number | null>(null);

const reverifyRemotePendingBusy = computed(() => {
    const row = reverifyScopedPendingData.value;
    return Boolean(
        row?.pending === true &&
            typeof row?.operation_id === 'string' &&
            row.operation_id !== ''
    );
});

/** True while a queued deep re-verify for the open track is still in flight (POST, tracked op, or remote pending). */
const isRerunInProgressForThisTrack = computed(() => {
    const ours = normalizedTrackCode.value;
    return (
        reverifyPostRequestedForCodeUpper.value === ours ||
        (reverifyRerunOperationId.value !== null &&
            reverifyQueuedForTrackCodeUpper.value === ours) ||
        reverifyRemotePendingBusy.value
    );
});

const ddexRemotePendingBusy = computed(() => {
    const row = ddexScopedPendingData.value;
    return Boolean(
        row?.pending === true &&
            typeof row?.operation_id === 'string' &&
            row.operation_id !== ''
    );
});

const isDdexRerunBusyForThisTrack = computed(() => {
    const ours = normalizedTrackCode.value;
    return (
        ddexRerunPostRequestedForCodeUpper.value === ours ||
        (ddexRerunOperationId.value !== null &&
            ddexRerunQueuedForTrackCodeUpper.value === ours) ||
        ddexRemotePendingBusy.value
    );
});

const targetKbps = computed(() => data.value?.target_bitrate_kbps ?? 320);

const liveTrackId = computed(() => data.value?.track?.id ?? null);

/** Worst severity across loaded issues — matches {@link AudioFileIssueCard} badge row. */
const headlineWorstSeverity = computed<AudioFileIssueSeverity | null>(() => {
    const issues = data.value?.issues;
    if (issues == null || issues.length === 0) {
        return null;
    }
    const rank: Record<AudioFileIssueSeverity, number> = { error: 0, warning: 1, info: 2 };
    let worst: AudioFileIssueSeverity = 'info';
    for (const issue of issues) {
        if (rank[issue.severity] < rank[worst]) {
            worst = issue.severity;
        }
    }

    return worst;
});

const displayString = computed(() => {
    const t = data.value?.track;
    if (!t) return props.trackCode;
    const name = t.name ?? '';
    const remix = t.remix_name ?? '';
    if (!name) return t.code;

    return remix ? `${name} (${remix})` : name;
});

/** Cover-overlay play props so the inspector header cover gets the same play+expand affordance as the list card. */
const coverPlay = computed(() => ({
    kind: 'live_track' as const,
    // Unique testid so e2e can target this one cover play in any layout — in split
    // view it would otherwise collide with the per-card covers' `play-track-live_track`.
    testid: 'audio-file-issue-inspector-play',
    liveTrackCode: props.trackCode,
    hasAudio: Boolean(props.trackCode),
    title: displayString.value,
    artist: data.value?.track?.artist_name ?? null,
    album: data.value?.track?.album_name ?? null,
    coverUrl: data.value?.track?.cover_art_signed_url ?? null,
    // Record for the player bar's title line. The show payload carries the
    // artist and album as names only, so those two lines stay plain text.
    liveTrackId: data.value?.track?.id ?? null,
}));

function belowTargetDetected(kbps: number | null | undefined): boolean {
    if (kbps == null) return false;

    return kbps < targetKbps.value;
}

const canRestoreFromBackup = computed(() => data.value?.original_bitrate?.status === 'valid');

/**
 * The verifier's finding about this track's `-https` HLS master that makes a restore worth
 * confirming: `hls_master_unplayable`, or `hls_unreadable` when it could not read the track's
 * HLS files at all (the listing or the master). It records one or the other in a run, never
 * both (tabasco#767).
 *
 * Every unplayable master the SAU-335 census found came from an audio replacement,
 * and "Restore from backup" re-encodes the PRE-replacement original. The encoder has
 * been fixed since SAU-338, so the playlist comes back playable — but with the
 * pre-replacement audio: the finding clears because the replacement was silently
 * undone. An unreadable master was never found playable either, only unknown, so it
 * counts too; otherwise a re-verify whose read was refused would drop the warning.
 * Whether restoring is safe depends on whether the audio was ever replaced, which the
 * issue row cannot tell, so the button is deliberately not gated on this (a
 * never-replaced track still restores legitimately). The restore is confirmed instead,
 * with the finding in view — see {@link requestRestoreFromBackup}.
 */
const hlsMasterFinding = computed<AudioFileIssueRow | null>(() => {
    const issues = data.value?.issues ?? [];
    const unplayable = issues.find((issue) => issue.issue_type === 'hls_master_unplayable');
    if (unplayable) {
        return unplayable;
    }

    return issues.find((issue) => issue.issue_type === 'hls_unreadable') ?? null;
});

/** The `details.remedy` the verifier recorded with that finding. Only an unplayable master carries one. */
const hlsMasterFindingRemedy = computed<string | null>(() => {
    const remedy = hlsMasterFinding.value?.details?.remedy;

    return typeof remedy === 'string' && remedy.trim() !== '' ? remedy.trim() : null;
});

const RESTORE_UNDOES_REPLACEMENT =
    "If this track's audio was ever replaced, restoring re-encodes the original from before " +
    'that replacement: the playlist comes back playable, but with the pre-replacement audio, ' +
    'so the replacement is silently undone.';

const restoreFromBackupConfirmMessage = computed(() => {
    const finding = hlsMasterFinding.value;
    if (!finding) return '';

    if (finding.issue_type === 'hls_unreadable') {
        // A lasting read failure: no playability verdict, and no remedy recorded with it.
        return (
            `The verifier recorded “${finding.issue_type_label}” for this track: it could not ` +
            'read its HLS files, so whether the physical players can play it is unknown. ' +
            RESTORE_UNDOES_REPLACEMENT
        );
    }

    const remedy = hlsMasterFindingRemedy.value;
    const remedyLine = remedy
        ? `Recorded remedy: ${remedy}`
        : 'The verifier recorded no remedy for this finding.';

    return (
        `The verifier recorded “${finding.issue_type_label}” for this track. ` +
        `${RESTORE_UNDOES_REPLACEMENT} ${remedyLine}`
    );
});

/**
 * Whether the confirmation is open, and the only thing that lets its confirm send. Every way
 * the dialog closes sets it to false: its own confirm and Cancel, then Escape, the X and a
 * click outside through `v-model:open`, and the withdrawals below. The dialog stays mounted
 * and clickable through its exit animation, so a click that lands there finds it false and
 * sends nothing — see {@link confirmRestoreFromBackup}.
 */
const showRestoreFromBackupConfirm = ref(false);

// The dialog describes one finding. The by-track query can refetch while it is open (a
// re-verify finishing over Echo); if that run records neither finding, the message the
// operator is looking at is void, so the confirmation is withdrawn and the next click
// decides afresh. A swap from unplayable to unreadable keeps it open, and the message
// follows the finding.
watch(hlsMasterFinding, (finding) => {
    if (finding === null) {
        showRestoreFromBackupConfirm.value = false;
    }
});

const NO_VALID_BACKUP_MESSAGE = 'The backup is no longer valid. Nothing was queued.';

// Likewise when that refetch reports the backup missing or unreadable: the finding may still
// be there, but nothing can be restored, and a dialog left open would let "Restore anyway"
// do nothing without a word. Withdraw it and say why.
watch(canRestoreFromBackup, (canRestore) => {
    if (!canRestore && showRestoreFromBackupConfirm.value) {
        showRestoreFromBackupConfirm.value = false;
        toast.error(NO_VALID_BACKUP_MESSAGE);
    }
});

const canLoadBackup = computed(() => data.value?.original_bitrate?.status === 'valid');

// "Present" = inspection recorded a real backup object (status valid OR invalid).
// "missing" (and a null/undefined original_bitrate) both mean no backup file to read from,
// so backup-only metadata rows should be hidden rather than rendered as blanks.
const backupFilePresent = computed(() => {
    const status = data.value?.original_bitrate?.status;

    return status === 'valid' || status === 'invalid';
});

const backupProgressPercent = computed(() => {
    if (loadBackupPhase.value !== 'download') {
        return 0;
    }
    const total = backupDownloadTotal.value;
    const loaded = backupDownloadLoaded.value;
    if (total != null && total > 0 && loaded >= 0) {
        return Math.min(100, Math.round((loaded / total) * 100));
    }

    return 0;
});

function revokeBackupCover(): void {
    const url = backupTags.value?.coverObjectUrl;
    if (url) {
        URL.revokeObjectURL(url);
    }
}

function revokeManualCover(): void {
    const url = manualUploadTags.value?.coverObjectUrl;
    if (url) {
        URL.revokeObjectURL(url);
    }
}

function resetBackupLoadState(): void {
    revokeBackupCover();
    backupTags.value = null;
    loadBackupPhase.value = 'idle';
    backupDownloadLoaded.value = 0;
    backupDownloadTotal.value = null;
}

function resetManualUploadState(): void {
    revokeManualCover();
    manualUploadTags.value = null;
    pendingManualFile.value = null;
    manualUploadParsing.value = false;
    manualDropHover.value = false;
}

watch(
    () => props.trackCode,
    () => {
        resetBackupLoadState();
        resetManualUploadState();
        // Split view and the standalone route reuse this instance across tracks. A
        // confirmation opened for the previous track must not survive into this one,
        // where "Restore anyway" would restore the wrong code.
        showRestoreFromBackupConfirm.value = false;
    }
);

onBeforeUnmount(() => {
    revokeBackupCover();
    revokeManualCover();
});

async function play(source: 'live_mp3' | 'live_hls' | 'backup_mp3') {
    // For the live MP3 / HLS sources, the artist + album shown in the global
    // player come from the LiveTrack record (the canonical metadata). For the
    // backup source they come from the backup file's own ID3 tags when we have
    // them — that audio may pre-date later metadata edits, so the on-file tags
    // are the truer label for what's actually being played.
    const trackArtist = data.value?.track?.artist_name ?? null;
    const trackAlbum = data.value?.track?.album_name ?? null;
    const artist = source === 'backup_mp3'
        ? (backupTags.value?.artist ?? trackArtist)
        : trackArtist;
    const album = source === 'backup_mp3'
        ? (backupTags.value?.album ?? trackAlbum)
        : trackAlbum;
    try {
        await playback.loadAndPlay({
            code: props.trackCode,
            title: displayString.value,
            artist,
            album,
            coverUrl: data.value?.track?.cover_art_signed_url ?? null,
            source,
        });
    } catch {
        toast.error('Could not start playback.');
    }
}

async function playPendingManualFile() {
    const file = pendingManualFile.value;
    if (!file || manualUploadParsing.value || uploading.value || applyMutation.isPending.value) {
        return;
    }
    try {
        await playback.loadAndPlayLocalFile({
            file,
            code: props.trackCode,
            title: manualUploadTags.value?.title?.trim() || displayString.value,
            // The pending file's own ID3 tags describe what the user is about
            // to hear — they may differ from the LiveTrack record's metadata,
            // and the player should reflect the bytes being played.
            artist: manualUploadTags.value?.artist ?? data.value?.track?.artist_name ?? null,
            album: manualUploadTags.value?.album ?? data.value?.track?.album_name ?? null,
            coverUrl: manualUploadTags.value?.coverObjectUrl ?? null,
        });
    } catch {
        toast.error('Could not start playback.');
    }
}

async function reverify() {
    const ours = normalizedTrackCode.value;
    if (!ours || isRerunInProgressForThisTrack.value) {
        return;
    }

    reverifyPostRequestedForCodeUpper.value = ours;
    try {
        const response = await rerunMutation.mutateAsync({ code: props.trackCode });
        reverifyQueuedForTrackCodeUpper.value = ours;
        reverifyFinalizedOperationIds.delete(response.meta.async_operation_id);
        reverifyRerunOperationId.value = response.meta.async_operation_id;
        // Kick the pending query so polling tracks this operation immediately — the Echo
        // subscribe handshake can lose the race with the broadcast for fast jobs.
        void queryClient.invalidateQueries({
            queryKey: scopedPendingStaticQueryKey(VERIFICATION_RERUN_ASYNC_TYPE, ours),
        });
        toast.message(
            response.meta.reused_in_flight_operation
                ? 'Re-verification is still in progress'
                : 'Re-verification queued'
        );
    } catch (unknownError: unknown) {
        const axiosData = unknownError as {
            response?: { data?: { message?: string; errors?: Record<string, string[]> } };
            message?: string;
        };
        const firstField = axiosData.response?.data?.errors
            ? Object.values(axiosData.response.data.errors)[0]?.[0]
            : undefined;
        const msg =
            firstField ??
            axiosData.response?.data?.message ??
            (unknownError instanceof Error ? unknownError.message : undefined) ??
            'Could not queue re-verification.';
        toast.error(msg);
    } finally {
        reverifyPostRequestedForCodeUpper.value = null;
    }
}

async function rerunDdexMatching() {
    const ours = normalizedTrackCode.value;
    if (
        ours === '' ||
        isLoading.value ||
        ddexMatchesLoading.value ||
        ddexMatchesError.value != null ||
        isDdexRerunBusyForThisTrack.value
    ) {
        return;
    }

    ddexRerunPostRequestedForCodeUpper.value = ours;
    try {
        const response = await ddexRerunMutation.mutateAsync({ code: props.trackCode });
        ddexRerunQueuedForTrackCodeUpper.value = ours;
        ddexRerunFinalizedOperationIds.delete(response.meta.async_operation_id);
        ddexRerunOperationId.value = response.meta.async_operation_id;
        // Kick the pending query so polling tracks this operation immediately — the Echo
        // subscribe handshake can lose the race with the broadcast for fast jobs.
        void queryClient.invalidateQueries({
            queryKey: scopedPendingStaticQueryKey(DDEX_MATCH_RERUN_ASYNC_TYPE, ours),
        });
        toast.message(
            response.meta.reused_in_flight_operation
                ? 'DDEX matching is still in progress'
                : 'DDEX matching queued'
        );
    } catch (unknownError: unknown) {
        const axiosData = unknownError as {
            response?: { data?: { message?: string; errors?: Record<string, string[]> } };
            message?: string;
        };
        const firstField = axiosData.response?.data?.errors
            ? Object.values(axiosData.response.data.errors)[0]?.[0]
            : undefined;
        const msg =
            firstField ??
            axiosData.response?.data?.message ??
            (unknownError instanceof Error ? unknownError.message : undefined) ??
            'Could not queue DDEX matching.';
        toast.error(msg);
    } finally {
        ddexRerunPostRequestedForCodeUpper.value = null;
    }
}

/**
 * "Restore from backup" click. When the HLS master is unplayable or unreadable the restore
 * is confirmed first, with the verifier's finding in view; otherwise it is sent straight away.
 */
function requestRestoreFromBackup() {
    if (!canRestoreFromBackup.value || applyMutation.isPending.value) return;

    if (hlsMasterFinding.value) {
        showRestoreFromBackupConfirm.value = true;

        return;
    }

    void restoreFromBackup();
}

/**
 * The dialog's confirm. `ConfirmDialog` emits `confirm` before it closes itself, so the click
 * that confirms finds the dialog open and sends. Any later click, or one after Cancel, Escape,
 * the X or a withdrawal, lands on a closing dialog and is ignored.
 */
function confirmRestoreFromBackup() {
    if (!showRestoreFromBackupConfirm.value) return;

    void restoreFromBackup();
}

async function restoreFromBackup() {
    if (!canRestoreFromBackup.value) {
        // Unreachable through the UI — the button is disabled and the dialog withdrawn without
        // a valid backup — but loud rather than silent should a path ever skip both.
        toast.error(NO_VALID_BACKUP_MESSAGE);

        return;
    }
    // One request at a time. vue-query reports `isPending` in the same tick `mutateAsync` is
    // called (useApplyAudioFix.test.ts pins that), so a second activation already sees it.
    // The dialog has its own guard as well — see {@link confirmRestoreFromBackup}.
    if (applyMutation.isPending.value) return;

    try {
        const res = await applyMutation.mutateAsync({
            code: props.trackCode,
            source: 'backup',
        });

        toast.success('Restore queued.', {
            description: `Live track action #${res.live_track_action_id}`,
        });
    } catch {
        toast.error('Could not queue restore.');
    }
}

function openManualFilePicker() {
    manualFileInputRef.value?.click();
}

const manualDropHover = ref(false);

function onManualDragEnter(ev: DragEvent) {
    ev.preventDefault();
    if (ev.dataTransfer?.types?.includes('Files')) {
        manualDropHover.value = true;
    }
}

function onManualDragLeave(ev: DragEvent) {
    ev.preventDefault();
    const el = ev.currentTarget as HTMLElement;
    const related = ev.relatedTarget as Node | null;
    if (!related || !el.contains(related)) {
        manualDropHover.value = false;
    }
}

function onManualDragOver(ev: DragEvent) {
    ev.preventDefault();
    if (ev.dataTransfer) {
        ev.dataTransfer.dropEffect = 'copy';
    }
}

function onManualDrop(ev: DragEvent) {
    ev.preventDefault();
    manualDropHover.value = false;
    if (!canPickManualFile.value) {
        return;
    }
    const files = ev.dataTransfer?.files;
    if (!files?.length) {
        return;
    }
    const file = files[0];
    const name = file.name.toLowerCase();
    const typeOk =
        file.type === 'audio/mpeg' ||
        file.type === 'audio/mp3' ||
        name.endsWith('.mp3');
    if (!typeOk) {
        toast.error('Please drop an MP3 file.');
        return;
    }
    void assignManualFile(file);
}

async function assignManualFile(file: File): Promise<void> {
    revokeManualCover();
    manualUploadTags.value = null;
    pendingManualFile.value = file;
    manualUploadParsing.value = true;

    try {
        const tags = await parseBackupMp3Metadata(file);
        manualUploadTags.value = tags;
    } catch {
        manualUploadTags.value = null;
        toast.error(
            'Could not read ID3 tags from this file. You can still upload it as the replacement.'
        );
    } finally {
        manualUploadParsing.value = false;
    }
}

async function onManualFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
        return;
    }

    await assignManualFile(file);
}

function ddexNestedText(value: unknown): string {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        if (first && typeof first === 'object' && first !== null) {
            if ('display_string' in first && (first as { display_string: unknown }).display_string != null) {
                return String((first as { display_string: unknown }).display_string);
            }
            if ('name' in first && typeof (first as { name: unknown }).name === 'string') {
                return (first as { name: string }).name;
            }
        }
    }
    if (typeof value === 'object' && value !== null && 'display_string' in value) {
        return String((value as { display_string: unknown }).display_string ?? '');
    }

    return '';
}

function ddexRowTitle(row: AudioFileIssueDdexMatchRow): string {
    const rawName = row.matched_track.name;
    const name = typeof rawName === 'string' ? rawName : '';
    return name || 'Unknown title';
}

function ddexRowRemix(row: AudioFileIssueDdexMatchRow): string {
    const raw = row.matched_track.remix_name;
    return typeof raw === 'string' ? raw : '';
}

function ddexDisplayTitle(row: AudioFileIssueDdexMatchRow): string {
    const name = ddexRowTitle(row);
    const remix = ddexRowRemix(row);
    if (!remix) {
        return name;
    }

    return `${name} (${remix})`;
}

function ddexRowArtist(row: AudioFileIssueDdexMatchRow): string {
    return ddexNestedText(row.matched_track.artist) || 'Unknown artist';
}

function ddexRowAlbum(row: AudioFileIssueDdexMatchRow): string {
    return ddexNestedText(row.matched_track.album) || 'Unknown album';
}

function ddexRowCover(row: AudioFileIssueDdexMatchRow): string | null {
    const u = row.matched_track.cover_art_signed_url;
    return typeof u === 'string' && u !== '' ? u : null;
}

function ddexRowDurationSeconds(row: AudioFileIssueDdexMatchRow): number | null {
    const d = row.matched_track.duration;
    if (typeof d === 'number' && Number.isFinite(d)) {
        return d;
    }

    return null;
}

function ddexRowBitrate(row: AudioFileIssueDdexMatchRow): number | null {
    const b = row.matched_track.bitrate;
    if (typeof b === 'number' && Number.isFinite(b)) {
        return b;
    }

    return null;
}

// LiveTrackMatchDTO stores 0 when the source has no copyright year; treat that as "no year".
function ddexRowCopyright(row: AudioFileIssueDdexMatchRow): number | null {
    const c = row.matched_track.copyright;
    if (typeof c === 'number' && Number.isFinite(c) && c > 0) {
        return c;
    }

    return null;
}

function ddexRowIsrc(row: AudioFileIssueDdexMatchRow): string {
    const i = row.matched_track.isrc;
    return typeof i === 'string' ? i : '';
}

function ddexRowMatchedCreatedIso(row: AudioFileIssueDdexMatchRow): string | null {
    const rawCreated = row.matched_track.created_at;
    return typeof rawCreated === 'string' && rawCreated !== '' ? rawCreated : null;
}

function ddexRowHasPlayableAudio(row: AudioFileIssueDdexMatchRow): boolean {
    const p = row.matched_track.track_file_path;
    return typeof p === 'string' && p.startsWith('s3://');
}

function isDdexRowLinked(row: AudioFileIssueDdexMatchRow): boolean {
    const linked = data.value?.track?.raw_ddex_track_id;
    if (linked == null) {
        return false;
    }

    return linked === row.raw_ddex_track_id;
}

async function playDdex(row: AudioFileIssueDdexMatchRow) {
    if (!ddexRowHasPlayableAudio(row) || ddexPlayLoadingId.value !== null) {
        return;
    }

    ddexPlayLoadingId.value = row.raw_ddex_track_id;
    try {
        const descriptor = await fetchRawDdexTrackAudioDescriptor(row.raw_ddex_track_id);
        await playback.loadAndPlayWithDescriptor({
            code: props.trackCode,
            title: ddexDisplayTitle(row),
            // ddexRow* helpers fall back to "Unknown artist" / "Unknown album"
            // when the nested DDEX ref is empty — that's the same fallback the
            // player template uses, so the strings line up either way.
            artist: ddexRowArtist(row),
            album: ddexRowAlbum(row),
            coverUrl: ddexRowCover(row),
            source: 'ddex_mp3',
            descriptor,
        });
    } catch {
        toast.error('Could not start DDEX playback.');
    } finally {
        if (ddexPlayLoadingId.value === row.raw_ddex_track_id) {
            ddexPlayLoadingId.value = null;
        }
    }
}

async function ingestDdex(row: AudioFileIssueDdexMatchRow) {
    const id = liveTrackId.value;
    if (id == null || linkDdexMutation.isPending.value) {
        return;
    }

    try {
        await linkDdexMutation.mutateAsync({
            code: props.trackCode,
            liveTrackId: id,
            rawDdexTrackId: row.raw_ddex_track_id,
        });
        toast.success('DDEX track linked.', {
            description: `Raw DDEX track #${row.raw_ddex_track_id}`,
        });
        await refetch();
        await refetchDdexMatches();
    } catch {
        toast.error('Could not link DDEX track.');
    }
}

async function loadBackup(): Promise<void> {
    if (!canLoadBackup.value || loadBackupInFlight.value) {
        return;
    }

    resetBackupLoadState();
    loadBackupInFlight.value = true;
    loadBackupPhase.value = 'presign';
    try {
        const descriptor = await fetchBackupPresignedDescriptor(props.trackCode);
        loadBackupPhase.value = 'download';
        backupDownloadLoaded.value = 0;
        backupDownloadTotal.value =
            descriptor.content_length != null && descriptor.content_length > 0
                ? descriptor.content_length
                : null;
        const blob = await downloadPresignedUrlAsBlob(
            descriptor.url,
            descriptor.content_length,
            (loaded, total) => {
                backupDownloadLoaded.value = loaded;
                if (total != null && total > 0) {
                    backupDownloadTotal.value = total;
                }
            }
        );
        loadBackupPhase.value = 'parse';
        const tags = await parseBackupMp3Metadata(blob);
        backupTags.value = tags;
        loadBackupPhase.value = 'done';
        toast.success('Backup loaded.');
    } catch (err) {
        loadBackupPhase.value = 'error';
        toast.error(err instanceof Error ? err.message : 'Could not load backup.');
    } finally {
        loadBackupInFlight.value = false;
    }
}

async function submitManualUpload() {
    const file = pendingManualFile.value;
    if (!file || uploading.value || applyMutation.isPending.value) {
        return;
    }

    uploading.value = true;
    try {
        const presign = await presignMutation.mutateAsync({ code: props.trackCode });

        await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', presign.url);

            xhr.setRequestHeader('Content-Type', 'audio/mpeg');

            xhr.upload.onprogress = () => {};

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed (${xhr.status})`));
                }
            };
            xhr.onerror = () => reject(new Error('Upload failed'));

            xhr.send(file);
        });

        await applyMutation.mutateAsync({
            code: props.trackCode,
            source: 'uploaded',
            s3_key: presign.key,
        });

        toast.success('Replacement queued.', {
            description: 'Live track job dispatched.',
        });
        resetManualUploadState();
    } catch {
        toast.error('Upload or apply failed.');
    } finally {
        uploading.value = false;
    }
}

/** `MM/DD/YY h:mm AM/PM` for Audio File Issues inspector surfaces (em dash when missing). */
function ts12(iso: string | null | undefined): string {
    return formatUsMmDdYyTime12h(iso ?? undefined) || '—';
}

function formatDuration(totalSeconds: number | null | undefined): string {
    if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
        return '';
    }

    const totalWhole = Math.round(totalSeconds);
    const minutes = Math.floor(totalWhole / 60);
    const seconds = totalWhole % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatOriginalBitrateStatus(status: string | null | undefined): string {
    if (!status) {
        return 'Unknown';
    }

    // `split('_').join(' ')` is the ES5-compatible equivalent of
    // `String.prototype.replaceAll('_', ' ')`. Used here so the file compiles
    // against the project's current `lib` target without needing a tsconfig
    // bump just for one call site.
    return status.split('_').join(' ');
}

const trackUploadedIso = computed(
    () => data.value?.track?.uploaded_date ?? data.value?.track?.file_updated_at ?? null
);

const backupLoadStatusLabel = computed(() => {
    switch (loadBackupPhase.value) {
        case 'presign':
            return 'Getting download link…';
        case 'download':
            return 'Downloading backup…';
        case 'parse':
            return 'Parsing tags…';
        default:
            return '';
    }
});

function formatByteCount(n: number): string {
    if (!Number.isFinite(n) || n < 0) {
        return '0';
    }
    if (n < 1024) {
        return `${n} B`;
    }
    if (n < 1024 * 1024) {
        return `${(n / 1024).toFixed(1)} KB`;
    }

    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const canSubmitManualUpload = computed(
    () =>
        pendingManualFile.value != null &&
        !manualUploadParsing.value &&
        !uploading.value &&
        !applyMutation.isPending.value
);

const canPickManualFile = computed(
    () =>
        normalizedTrackCode.value.length > 0 &&
        !uploading.value &&
        !applyMutation.isPending.value
);
</script>

<template>
    <div class="@container flex min-h-0 flex-col gap-6">
        <LiveTrackCard
            card-class="shrink-0 overflow-hidden p-0"
            body-class="p-4"
            row-align="stretch"
            cover-meta-to-bottom
            show-zero-copyright
            fallback-on-empty
            :meta-truncate="false"
            title-tag="h2"
            :cover-url="data?.track?.cover_art_signed_url"
            :copyright="data?.track?.copyright"
            :duration="formatDuration(data?.track?.duration)"
            :title="displayString"
            :artist="data?.track?.artist_name"
            :album="data?.track?.album_name"
            :cover-play="coverPlay"
            :cover-expand-testid="`audio-file-issue-inspector-expand-cover-${props.trackCode}`"
        >
            <!-- Title + badges (below album) + bitrates — mirrors the list card's right column. -->
            <template #badges>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                    <SeverityBadge
                        v-if="headlineWorstSeverity != null"
                        :severity="headlineWorstSeverity"
                        size="md"
                        class="text-sm font-semibold"
                    />
                    <TrackRatingBadge
                        :rating="data?.track?.rating"
                        class="text-sm font-semibold"
                    />
                    <span
                        v-if="data?.track?.genre_name"
                        class="text-muted-foreground truncate text-sm"
                    >
                        {{ data.track.genre_name }}
                    </span>
                </div>
            </template>
            <template #meta>
                <div
                    class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2 font-mono text-[11px] leading-none tabular-nums"
                >
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <span class="whitespace-nowrap">
                                {{ data?.gvlibrary?.detected_bitrate_kbps ?? '?' }} kbps
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>File Bitrate</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger as-child>
                            <span class="whitespace-nowrap">
                                {{ data?.track?.bitrate ?? '?' }} kbps
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>DB Bitrate</TooltipContent>
                    </Tooltip>
                    <span v-if="data?.track?.isrc" class="whitespace-nowrap">
                        ISRC {{ data.track.isrc }}
                    </span>
                    <Tooltip v-if="data?.track?.created_at">
                        <TooltipTrigger as-child>
                            <span class="whitespace-nowrap tabular-nums">{{
                                ts12(data.track.created_at ?? undefined)
                            }}</span>
                        </TooltipTrigger>
                        <TooltipContent align="start" class="space-y-0.5" side="bottom">
                            <p class="text-xs font-medium">Track Created</p>
                            <p class="text-muted-foreground text-xs tabular-nums">{{
                                ts12(data.track.created_at ?? undefined)
                            }}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip v-if="data?.track?.track_uploaded_date">
                        <TooltipTrigger as-child>
                            <span class="whitespace-nowrap tabular-nums">{{
                                ts12(data.track.track_uploaded_date ?? undefined)
                            }}</span>
                        </TooltipTrigger>
                        <TooltipContent align="start" class="space-y-0.5" side="bottom">
                            <p class="text-xs font-medium">Track uploaded</p>
                            <p class="text-muted-foreground text-xs tabular-nums">{{
                                ts12(data.track.track_uploaded_date ?? undefined)
                            }}</p>
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip v-if="trackUploadedIso">
                        <TooltipTrigger as-child>
                            <span class="whitespace-nowrap tabular-nums">{{ ts12(trackUploadedIso) }}</span>
                        </TooltipTrigger>
                        <TooltipContent align="start" class="space-y-0.5" side="bottom">
                            <p class="text-xs font-medium">File uploaded</p>
                            <p class="text-muted-foreground text-xs tabular-nums">{{ ts12(trackUploadedIso) }}</p>
                        </TooltipContent>
                    </Tooltip>
                    <!-- "Live file updated" — when the live gvlibrary file was last refreshed.
                         Carried by the old Play MP3 / Play HLS buttons' tooltip; restored here
                         as a header chip after those buttons became the Option-key cover play. -->
                    <Tooltip v-if="data?.track?.file_updated_at">
                        <TooltipTrigger as-child>
                            <span
                                class="whitespace-nowrap tabular-nums"
                                data-testid="inspector-live-file-updated"
                            >{{ ts12(data.track.file_updated_at) }}</span>
                        </TooltipTrigger>
                        <TooltipContent align="start" class="space-y-0.5" side="bottom">
                            <p class="text-xs font-medium">Live file updated</p>
                            <p class="text-muted-foreground text-xs tabular-nums">{{
                                ts12(data.track.file_updated_at)
                            }}</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </template>
            <!-- Wide layouts: keep playback + re-verify in a footer so the title column can use full width. -->
            <template #footer>
                <div
                    class="flex flex-wrap items-center justify-start gap-2 border-t border-border/60 bg-muted/20 px-4 py-3"
                    aria-label="Re-verify action"
                >
                    <Button
                        size="sm"
                        :disabled="isLoading || isRerunInProgressForThisTrack"
                        @click="reverify"
                    >
                        {{ isRerunInProgressForThisTrack ? 'Re-verifying…' : 'Re-verify' }}
                    </Button>
                </div>
            </template>
        </LiveTrackCard>

        <Tabs v-model="inspectorTab" class="flex min-h-0 flex-1 flex-col gap-4">
            <InspectorTabsMenu
                :model-value="inspectorTab"
                :items="INSPECTOR_TAB_ITEMS"
                @update:model-value="onInspectorTabSelect"
            />

            <TabsContent value="issues" class="mt-0 flex min-h-0 flex-1 flex-col gap-4 outline-none">

                <div v-if="error" class="border-destructive/50 bg-destructive/10 text-destructive shrink-0 rounded-md border p-3 text-sm">
                    Could not load issues for this track.
                </div>

                <div v-if="isLoading" class="text-muted-foreground shrink-0 text-sm">Loading…</div>

                <div v-else-if="!data || data.issues.length === 0" class="text-muted-foreground shrink-0 text-sm">
                    No issues recorded for this track.
                </div>

                <div v-else class="flex min-h-0 flex-1 flex-col gap-3">
                    <p class="text-muted-foreground text-xs font-semibold uppercase tracking-wide">Issues for this track</p>
                    <div class="max-h-[min(52vh,560px)] shrink-0 overflow-y-auto pr-1">
                        <ul class="flex flex-col gap-3 pb-1" role="list">
                            <li v-for="issue in data.issues" :key="issue.id">
                                <Card class="@container gap-0 border-border/80 p-4 shadow-sm">
                                    <!-- Row layout follows each card's width (narrow inspector), not the viewport. -->
                                    <div
                                        class="flex flex-col gap-2 @sm:flex-row @sm:items-baseline @sm:justify-between @sm:gap-4"
                                    >
                                        <h3 class="text-foreground min-w-0 flex-1 text-sm font-semibold leading-snug">
                                            {{ issue.issue_type_label }}
                                        </h3>
                                        <div
                                            class="text-muted-foreground flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 tabular-nums @sm:justify-end"
                                        >
                                            <SeverityBadge
                                                :severity="issue.severity"
                                                :label="issue.severity_label"
                                            />
                                            <Tooltip v-if="issue.verified_at">
                                                <TooltipTrigger as-child>
                                                    <span class="cursor-default text-xs tabular-nums">{{
                                                        ts12(issue.verified_at ?? undefined)
                                                    }}</span>
                                                </TooltipTrigger>
                                                <TooltipContent align="start" class="space-y-0.5" side="bottom">
                                                    <p class="text-xs font-medium">Verified</p>
                                                    <p class="text-muted-foreground text-xs tabular-nums">{{
                                                        ts12(issue.verified_at ?? undefined)
                                                    }}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div
                                        v-if="trackUploadedIso"
                                        class="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] leading-none tabular-nums"
                                    >
                                        <Tooltip v-if="trackUploadedIso">
                                            <TooltipTrigger as-child>
                                                <span class="whitespace-nowrap tabular-nums">{{
                                                    ts12(trackUploadedIso)
                                                }}</span>
                                            </TooltipTrigger>
                                            <TooltipContent align="start" class="space-y-0.5" side="bottom">
                                                <p class="text-xs font-medium">File uploaded</p>
                                                <p class="text-muted-foreground text-xs tabular-nums">{{
                                                    ts12(trackUploadedIso)
                                                }}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                    <InspectorDetailList
                                        v-if="issueDetailEntries(issue.details).length"
                                        class="mt-2.5"
                                    >
                                        <InspectorDetailRow
                                            v-for="entry in issueDetailEntries(issue.details)"
                                            :key="entry.key"
                                            :label="entry.key"
                                            bordered
                                            label-class="shrink-0 text-xs"
                                            value-class="min-w-0 flex-1 text-right text-sm"
                                        >{{ entry.value }}</InspectorDetailRow>
                                    </InspectorDetailList>
                                </Card>
                            </li>
                        </ul>
                    </div>
                </div>
            </TabsContent>

            <TabsContent value="recover-backup" class="mt-0 flex flex-col gap-4 outline-none">
                <!-- Order: Load backup button -> in-flight progress -> loaded ID3 card -> backup-on-record details.
                     The progress bar and the loaded card sit between the button and the details panel so the
                     details get pushed down naturally as state advances. -->
                <div class="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
                    <Button
                        variant="default"
                        :disabled="isLoading || !canLoadBackup || loadBackupInFlight || applyMutation.isPending.value"
                        @click="loadBackup"
                    >
                        Load backup
                    </Button>
                </div>

                <div
                    v-if="loadBackupPhase !== 'idle' && loadBackupPhase !== 'done' && loadBackupPhase !== 'error'"
                    class="flex flex-col gap-2 rounded-md border border-border/80 bg-muted/30 p-3"
                >
                    <p class="text-muted-foreground text-xs font-medium">
                        {{ backupLoadStatusLabel }}
                    </p>
                    <Progress v-if="loadBackupPhase === 'download'" :model-value="backupProgressPercent" />
                    <p v-if="loadBackupPhase === 'download'" class="text-muted-foreground font-mono text-[11px] tabular-nums">
                        {{ formatByteCount(backupDownloadLoaded) }}
                        <template v-if="backupDownloadTotal != null && backupDownloadTotal > 0">
                            / {{ formatByteCount(backupDownloadTotal) }}
                            ({{ backupProgressPercent }}%)
                        </template>
                    </p>
                </div>

                <Card
                    v-if="backupTags && loadBackupPhase !== 'error'"
                    class="@container gap-0 overflow-hidden border-border/80 p-0 py-0 shadow-sm"
                >
                    <CardContent class="p-4">
                        <p class="text-muted-foreground mb-3 text-xs font-semibold uppercase tracking-wide">
                            Backup file (ID3)
                        </p>
                        <!-- Layout matches {@link CardContent} / {@link CardFooter} DDEX match cards: cover column
                             with © + duration under the tile; title + tag-derived bitrate in the right column. -->
                        <div class="flex flex-col gap-3 @xs:flex-row @xs:items-stretch">
                            <div class="flex w-24 shrink-0 flex-col">
                                <CoverArt
                                    :src="backupTags.coverObjectUrl ?? null"
                                    alt="Cover art from backup file"
                                />
                                <div
                                    class="text-muted-foreground mt-auto flex items-baseline pt-1 font-mono text-[11px] leading-none tabular-nums"
                                >
                                    <span v-if="backupTags.copyright && backupTags.copyright.trim() !== ''">
                                        ©{{ backupTags.copyright }}
                                    </span>
                                    <span class="ml-auto">{{
                                        formatDuration(backupTags.durationSeconds) || '—'
                                    }}</span>
                                </div>
                            </div>
                            <div class="flex min-w-0 flex-1 flex-col">
                                <h3 class="text-base font-semibold leading-tight">
                                    {{ backupTags.title || 'Unknown title' }}
                                </h3>
                                <p class="text-foreground text-sm leading-snug">
                                    {{ backupTags.artist || 'Unknown artist' }}
                                </p>
                                <p class="text-muted-foreground text-xs leading-snug">
                                    {{ backupTags.album || 'Unknown album' }}
                                </p>
                                <div
                                    class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2 font-mono text-[11px] leading-none tabular-nums"
                                >
                                    <span
                                        class="whitespace-nowrap"
                                        :class="
                                            belowTargetDetected(backupTags.bitrateKbps)
                                                ? 'text-amber-700 dark:text-amber-400'
                                                : ''
                                        "
                                    >
                                        {{ backupTags.bitrateKbps != null ? `${backupTags.bitrateKbps} kbps` : '?' }}
                                        <span v-if="belowTargetDetected(backupTags.bitrateKbps)">
                                            (below {{ targetKbps }})
                                        </span>
                                    </span>
                                    <Tooltip v-if="data?.original_backup_file?.last_modified_at">
                                        <TooltipTrigger as-child>
                                            <span class="max-w-[10rem] cursor-default truncate whitespace-nowrap">{{
                                                ts12(data.original_backup_file.last_modified_at ?? undefined)
                                            }}</span>
                                        </TooltipTrigger>
                                        <TooltipContent align="start" class="space-y-0.5" side="bottom">
                                            <p class="text-xs font-medium">Original Backup Created</p>
                                            <p class="text-muted-foreground text-xs tabular-nums">{{
                                                ts12(data.original_backup_file.last_modified_at ?? undefined)
                                            }}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                    <CardFooter
                        class="flex flex-wrap items-center justify-start gap-2 border-t border-border/60 bg-muted/25 px-4 py-3"
                    >
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    :disabled="isLoading || data?.original_bitrate?.status !== 'valid'"
                                    @click="play('backup_mp3')"
                                >
                                    Play backup
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent class="space-y-0.5">
                                <template v-if="data?.original_bitrate?.verified_at">
                                    <p class="text-xs font-medium">Backup verified</p>
                                    <p class="text-muted-foreground text-xs tabular-nums">{{
                                        ts12(data.original_bitrate.verified_at ?? undefined)
                                    }}</p>
                                </template>
                                <p v-else class="text-xs">Backup verification date unknown</p>
                            </TooltipContent>
                        </Tooltip>
                        <Button
                            size="sm"
                            variant="secondary"
                            :disabled="isLoading || !canRestoreFromBackup || applyMutation.isPending.value"
                            @click="requestRestoreFromBackup"
                        >
                            Restore from backup
                        </Button>
                    </CardFooter>
                </Card>

                <div
                    v-if="data?.original_bitrate || data?.original_backup_file"
                    class="@container rounded-md border border-border/80 bg-muted/20 p-4 text-sm"
                >
                    <InspectorDetailList
                        label-class="text-[11px] font-medium uppercase tracking-wide"
                    >
                        <InspectorDetailRow
                            v-if="data?.original_backup_file?.storage_key"
                            label="File name"
                            value-class="font-mono text-[11px] break-all"
                        >
                            {{ data.original_backup_file.storage_key.split('/').pop() }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_bitrate?.status"
                            label="Status"
                            value-class="font-medium capitalize"
                        >
                            {{ formatOriginalBitrateStatus(data.original_bitrate.status) }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_backup_file?.last_modified_at"
                            label="Original Backup Created"
                            value-class="font-mono tabular-nums text-[11px]"
                        >
                            {{ ts12(data.original_backup_file.last_modified_at ?? undefined) }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_bitrate?.verified_at"
                            label="Last verified"
                            value-class="font-mono tabular-nums text-[11px]"
                        >
                            {{ ts12(data.original_bitrate.verified_at ?? undefined) }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_bitrate?.detected_bitrate_kbps != null"
                            label="Detected bitrate"
                            value-class="font-mono tabular-nums"
                        >
                            {{ data.original_bitrate.detected_bitrate_kbps }} kbps
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="backupFilePresent && data?.track?.original_duration != null"
                            label="Duration"
                            value-class="font-mono tabular-nums"
                        >
                            {{ formatDuration(data.track.original_duration) }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_backup_file?.content_length_bytes != null"
                            label="File size"
                            value-class="font-mono tabular-nums"
                        >
                            {{ formatByteCount(data.original_backup_file.content_length_bytes) }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="backupFilePresent && data?.original_bitrate && data.original_bitrate.is_vbr != null"
                            label="VBR"
                        >
                            {{ data.original_bitrate.is_vbr ? 'Yes' : 'No' }}
                        </InspectorDetailRow>
                        <InspectorDetailRow
                            v-if="data?.original_bitrate?.object_etag && data?.original_backup_file?.etag"
                            label="ETags match"
                            :value-class="
                                data.original_bitrate.object_etag === data.original_backup_file.etag
                                    ? 'font-medium'
                                    : 'text-destructive font-semibold'
                            "
                            :value-title="`Object ETag: ${data.original_bitrate.object_etag}\nHEAD ETag: ${data.original_backup_file.etag}`"
                        >
                            {{
                                data.original_bitrate.object_etag === data.original_backup_file.etag
                                    ? 'Yes'
                                    : 'No'
                            }}
                        </InspectorDetailRow>
                    </InspectorDetailList>
                </div>
            </TabsContent>

            <TabsContent value="manual-upload" class="mt-0 flex flex-col gap-4 outline-none">
                <input
                    ref="manualFileInputRef"
                    type="file"
                    class="hidden"
                    data-testid="audio-file-issue-manual-upload"
                    accept="audio/mpeg,audio/mp3,.mp3"
                    @change="onManualFileSelected"
                />


                <Card
                    class="gap-0 overflow-hidden border-border/80 py-0 shadow-sm transition-colors"
                    :class="{ 'border-primary/60 bg-primary/5': manualDropHover }"
                    @dragenter.prevent="onManualDragEnter"
                    @dragleave.prevent="onManualDragLeave"
                    @dragover.prevent="onManualDragOver"
                    @drop.prevent="onManualDrop"
                >
                    <CardContent class="p-4">
                        <template v-if="!pendingManualFile">
                            <button
                                type="button"
                                class="flex min-h-[8.5rem] w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border/80 bg-muted/15 px-4 py-8 text-center text-sm text-muted-foreground transition-colors hover:bg-muted/35 disabled:pointer-events-none disabled:opacity-50"
                                :disabled="!canPickManualFile"
                                @click="openManualFilePicker"
                            >
                                <span>Drop an MP3 here, or click to choose</span>
                                <span class="text-[11px]">.mp3 · replaces audio for this code after upload</span>
                            </button>
                        </template>

                        <template v-else>
                            <p class="text-muted-foreground font-mono text-[11px] break-all">
                                {{ pendingManualFile.name }} · {{ formatByteCount(pendingManualFile.size) }}
                            </p>

                            <div v-if="manualUploadParsing" class="text-muted-foreground py-2 text-sm">
                                Reading ID3 tags…
                            </div>

                            <template v-else>
                                <!-- Same cover / title / bitrate layout as backup ID3 + DDEX match cards. -->
                                <div class="flex flex-col gap-3 @xs:flex-row @xs:items-stretch">
                                    <div class="flex w-24 shrink-0 flex-col">
                                        <CoverArt
                                            :src="manualUploadTags?.coverObjectUrl ?? null"
                                            alt="Cover art from selected file"
                                        />
                                        <div
                                            class="text-muted-foreground mt-auto flex items-baseline pt-1 font-mono text-[11px] leading-none tabular-nums"
                                        >
                                            <span
                                                v-if="
                                                    manualUploadTags?.copyright &&
                                                    manualUploadTags.copyright.trim() !== ''
                                                "
                                            >
                                                ©{{ manualUploadTags.copyright }}
                                            </span>
                                            <span class="ml-auto">{{
                                                formatDuration(manualUploadTags?.durationSeconds) || '—'
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="flex min-w-0 flex-1 flex-col">
                                        <h3 class="text-base font-semibold leading-tight">
                                            {{ manualUploadTags?.title || 'Unknown title' }}
                                        </h3>
                                        <p class="text-foreground text-sm leading-snug">
                                            {{ manualUploadTags?.artist || 'Unknown artist' }}
                                        </p>
                                        <p class="text-muted-foreground text-xs leading-snug">
                                            {{ manualUploadTags?.album || 'Unknown album' }}
                                        </p>
                                        <div
                                            class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2 font-mono text-[11px] leading-none tabular-nums"
                                        >
                                            <span
                                                v-if="
                                                    manualUploadTags != null &&
                                                    manualUploadTags.bitrateKbps != null
                                                "
                                                class="whitespace-nowrap"
                                                :class="
                                                    belowTargetDetected(manualUploadTags.bitrateKbps)
                                                        ? 'text-amber-700 dark:text-amber-400'
                                                        : ''
                                                "
                                            >
                                                {{ manualUploadTags.bitrateKbps }} kbps
                                                <span v-if="belowTargetDetected(manualUploadTags.bitrateKbps)">
                                                    (below {{ targetKbps }})
                                                </span>
                                            </span>
                                            <span v-else class="whitespace-nowrap">? kbps</span>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </CardContent>
                    <CardFooter
                        v-if="pendingManualFile && !manualUploadParsing"
                        class="flex flex-wrap items-center justify-start gap-2 border-t border-border/60 bg-muted/25 px-4 py-3"
                    >
                        <Button
                            size="sm"
                            variant="outline"
                            type="button"
                            :disabled="
                                pendingManualFile == null ||
                                manualUploadParsing ||
                                uploading ||
                                applyMutation.isPending.value
                            "
                            @click="playPendingManualFile"
                        >
                            Play
                        </Button>
                        <Button
                            size="sm"
                            variant="default"
                            type="button"
                            :disabled="!canSubmitManualUpload || uploading"
                            @click="submitManualUpload"
                        >
                            {{ uploading ? 'Uploading…' : 'Upload' }}
                        </Button>
                    </CardFooter>
                </Card>
            </TabsContent>

            <TabsContent value="ddex" class="mt-0 flex min-h-0 flex-1 flex-col gap-4 outline-none">
                <div class="flex shrink-0 flex-wrap items-center gap-2">
                    <Button
                        size="sm"
                        type="button"
                        variant="outline"
                        :disabled="
                            isLoading ||
                            ddexMatchesLoading ||
                            ddexMatchesError ||
                            isDdexRerunBusyForThisTrack
                        "
                        @click="rerunDdexMatching"
                    >
                        {{
                            isDdexRerunBusyForThisTrack
                                ? 'Re-running DDEX matching…'
                                : 'Re-run DDEX matching'
                        }}
                    </Button>
                </div>
                <div
                    v-if="ddexMatchesError"
                    class="border-destructive/50 bg-destructive/10 text-destructive shrink-0 rounded-md border p-3 text-sm"
                >
                    Could not load DDEX matches.
                </div>
                <div v-if="ddexMatchesLoading" class="text-muted-foreground text-sm">Loading DDEX matches…</div>
                <div
                    v-else-if="!ddexMatches || ddexMatches.length === 0"
                    class="text-muted-foreground text-sm"
                >
                    No stored Raw DDEX matches for this track.
                </div>
                <div v-else class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
                    <Card
                        v-for="row in ddexMatches"
                        :key="row.live_track_match_id"
                        class="@container gap-0 overflow-hidden border-border/80 p-0 py-0 shadow-sm"
                    >
                        <CardContent class="p-4">
                            <div class="flex flex-col gap-3 @xs:flex-row @xs:items-stretch">
                                <div class="flex w-24 shrink-0 flex-col">
                                    <CoverArt
                                        :src="ddexRowCover(row)"
                                        :alt="`Cover for ${ddexDisplayTitle(row)}`"
                                    />
                                    <div
                                        class="text-muted-foreground mt-auto flex items-baseline pt-1 font-mono text-[11px] leading-none tabular-nums"
                                    >
                                        <span v-if="ddexRowCopyright(row) != null">
                                            ©{{ ddexRowCopyright(row) }}
                                        </span>
                                        <span class="ml-auto">
                                            {{ formatDuration(ddexRowDurationSeconds(row)) || '—' }}
                                        </span>
                                    </div>
                                </div>
                                <div class="flex min-w-0 flex-1 flex-col">
                                    <h3 class="text-base font-semibold leading-tight">
                                        {{ ddexDisplayTitle(row) }}
                                    </h3>
                                    <p class="text-foreground text-sm leading-snug">
                                        {{ ddexRowArtist(row) }}
                                    </p>
                                    <p class="text-muted-foreground text-xs leading-snug">
                                        {{ ddexRowAlbum(row) }}
                                    </p>
                                    <div
                                        class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-2 font-mono text-[11px] leading-none tabular-nums"
                                    >
                                        <span class="whitespace-nowrap">
                                            {{ ddexRowBitrate(row) != null ? `${ddexRowBitrate(row)} kbps` : '?' }}
                                        </span>
                                        <span v-if="ddexRowIsrc(row)" class="whitespace-nowrap">
                                            ISRC {{ ddexRowIsrc(row) }}
                                        </span>
                                        <Tooltip v-if="ddexRowMatchedCreatedIso(row)">
                                            <TooltipTrigger as-child>
                                                <span class="whitespace-nowrap tabular-nums">{{
                                                    ts12(ddexRowMatchedCreatedIso(row) ?? undefined)
                                                }}</span>
                                            </TooltipTrigger>
                                            <TooltipContent align="start" class="space-y-0.5" side="bottom">
                                                <p class="text-xs font-medium">Track Created</p>
                                                <p class="text-muted-foreground text-xs tabular-nums">{{
                                                    ts12(ddexRowMatchedCreatedIso(row) ?? undefined)
                                                }}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                        <Tooltip v-if="row.message_date">
                                            <TooltipTrigger as-child>
                                                <span class="whitespace-nowrap tabular-nums">{{
                                                    ts12(row.message_date ?? undefined)
                                                }}</span>
                                            </TooltipTrigger>
                                            <TooltipContent align="start" class="space-y-0.5" side="bottom">
                                                <p class="text-xs font-medium">Message date</p>
                                                <p class="text-muted-foreground text-xs tabular-nums">{{
                                                    ts12(row.message_date ?? undefined)
                                                }}</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter
                            class="flex flex-col items-stretch gap-3 border-t border-border/60 bg-muted/25 px-4 py-3 @sm:flex-row @sm:items-center @sm:justify-between"
                        >
                            <div class="flex flex-wrap items-center justify-start gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    :disabled="
                                        isLoading ||
                                        !ddexRowHasPlayableAudio(row) ||
                                        ddexPlayLoadingId !== null
                                    "
                                    @click="playDdex(row)"
                                >
                                    {{
                                        ddexPlayLoadingId === row.raw_ddex_track_id
                                            ? 'Loading…'
                                            : 'Play'
                                    }}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    :disabled="
                                        isLoading ||
                                        liveTrackId == null ||
                                        linkDdexMutation.isPending.value ||
                                        isDdexRowLinked(row)
                                    "
                                    @click="ingestDdex(row)"
                                >
                                    {{ isDdexRowLinked(row) ? 'Restored' : 'Restore from DDEX' }}
                                </Button>
                            </div>
                            <div class="flex min-w-0 flex-wrap items-center justify-start gap-2 @sm:justify-end">
                                <span
                                    v-if="row.isrc_match"
                                    class="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                >
                                    ISRC match
                                </span>
                                <span
                                    v-if="row.fuzzy_match"
                                    class="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                >
                                    Fuzzy
                                </span>
                                <span
                                    v-if="row.confidence_score != null"
                                    class="text-muted-foreground font-mono text-[10px] tabular-nums"
                                >
                                    Score {{ row.confidence_score.toFixed(2) }}
                                </span>
                                <span
                                    v-if="isDdexRowLinked(row)"
                                    class="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                                >
                                    Linked
                                </span>
                            </div>
                        </CardFooter>
                    </Card>
                </div>
            </TabsContent>
        </Tabs>

        <ConfirmDialog
            v-model:open="showRestoreFromBackupConfirm"
            title="Restore from backup?"
            :message="restoreFromBackupConfirmMessage"
            confirm-text="Restore anyway"
            variant="destructive"
            :confirm-disabled="applyMutation.isPending.value"
            cancel-text="Cancel"
            @confirm="confirmRestoreFromBackup"
        />
    </div>
</template>
